import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type { TriggerCentralMappingVariant } from 'lumen/components/settings/central-mapping/dropdowns/triggers/trigger-central-mapping';
import SettingsCentralMappingDropdownsTriggersTriggerCentralMapping from 'lumen/components/settings/central-mapping/dropdowns/triggers/trigger-central-mapping';
import type { BarBased } from 'lumen/models/central-mapping/mapping-entry-market-segment';
import {
  AUTOMAPPING_STATUS,
  type AutomappingStatus,
} from 'lumen/utils/central-mapping-constants';

type DropdownBarBasedOption = Option & { value: BarBased };

interface SettingsCentralMappingDropdownsDropdownBarBasedSignature {
  Args: {
    autoBarBased: BarBased;
    barBased: BarBased;
    hideAutomappingVisuals?: boolean;
    isMixed?: boolean;
    isReadOnly?: boolean;
    isSelected?: boolean;
    setBarBased: (value: BarBased) => void;
    userBarBased: BarBased;
    variant: TriggerCentralMappingVariant;
  };
}

export default class SettingsCentralMappingDropdownsDropdownBarBased extends Component<SettingsCentralMappingDropdownsDropdownBarBasedSignature> {
  @service declare intl: Services['intl'];

  get breadcrumbs(): string[] {
    if (this.args.barBased === null) {
      return [];
    }

    if (!this.selectedOption) {
      return [];
    }

    return [this.selectedOption.label];
  }

  get mappingStatus(): AutomappingStatus {
    const { userBarBased, autoBarBased, hideAutomappingVisuals } = this.args;

    if (!isEmpty(userBarBased)) {
      return AUTOMAPPING_STATUS.USER_MAPPED;
    }

    if (!hideAutomappingVisuals && !isEmpty(autoBarBased)) {
      return AUTOMAPPING_STATUS.AUTO_MAPPED;
    }

    return AUTOMAPPING_STATUS.UNMAPPED;
  }

  get options(): DropdownBarBasedOption[] {
    const { intl } = this;
    const { hideAutomappingVisuals, autoBarBased } = this.args;
    const hasAutoMappedBarBased = autoBarBased !== null;

    const autoBarBasedYes = autoBarBased === true;
    const autoBarBasedNo = autoBarBased === false;

    const getIcon = (autoValue: boolean): IconName | undefined => {
      if (hideAutomappingVisuals) {
        return undefined;
      }
      return autoValue ? 'automation' : undefined;
    };

    const getUnsafeClassName = (autoValue: boolean): string | undefined => {
      if (hideAutomappingVisuals) {
        return undefined;
      }
      return autoValue ? 'mapping-dropdown__option--automation' : undefined;
    };

    const options: DropdownBarBasedOption[] = [
      {
        value: true,
        label: intl.t('general.yes'),
        icon: getIcon(autoBarBasedYes),
        unsafe_className: getUnsafeClassName(autoBarBasedYes),
      },
      {
        value: false,
        label: intl.t('general.no'),
        icon: getIcon(autoBarBasedNo),
        unsafe_className: getUnsafeClassName(autoBarBasedNo),
      },
    ];

    // Auto-mapped barBased can't be unmapped.
    if (!hasAutoMappedBarBased) {
      options.push({
        value: null,
        label: intl.t('general.unmapped'),
      });
    }

    return options;
  }

  get selectedOption(): DropdownBarBasedOption | null {
    return (
      this.options.find((option) => option.value === this.args.barBased) ?? null
    );
  }

  @action onChange(option: DropdownBarBasedOption): void {
    this.args.setBarBased(option.value);
  }

  <template>
    <PrismComboSelectInput
      @disabled={{@isReadOnly}}
      @onChange={{this.onChange}}
      @options={{this.options}}
      @unsafe_triggerComponent={{component
        SettingsCentralMappingDropdownsTriggersTriggerCentralMapping
        breadcrumbs=this.breadcrumbs
        isMixed=@isMixed
        isReadOnly=@isReadOnly
        isSelected=@isSelected
        mappingStatus=this.mappingStatus
        variant=@variant
      }}
      @value={{this.selectedOption}}
      data-test-id="dropdown-bar-based"
    />
  </template>
}
